import { IOption } from "@/types"
import { cn } from "@/utils"
import { autoUpdate, flip, offset, size, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react"
import clsx from "clsx"
import { HTMLAttributes, ReactNode, useEffect, useMemo, useState } from "react"
import { Control, Controller, FieldValues, Path } from "react-hook-form"
import { useIntl } from "react-intl"
import { DropdownIcon } from "./DropdownIcon"
import "./style.sass"

export interface ISelectorProps<T extends string | number = string, Meta extends object = object> {
  options: IOption<T, Meta>[]
  value?: IOption<T, Meta> | string
  onChange?: (value: IOption<T, Meta>) => void
  classNames?: Partial<{
    control: string
    list: string
  }>
  renderOptions?: (option: IOption<T, Meta>, props: HTMLAttributes<HTMLDivElement> & { key: any }) => ReactNode
  leftSection?: ((value: string) => ReactNode) | ReactNode
  rightSection?: ReactNode
  fullWidth?: boolean
  variant?: "outlined" | "text" | "contained"
  searchable?: boolean
  placeholder?: string
  getValueLabel?: (value?: IOption<T, Meta> | string) => ReactNode
  disabled?: boolean
  error?: boolean
}

export const Selector = <T extends string | number = string, Meta extends object = object>({
  renderOptions,
  leftSection,
  rightSection,
  value,
  options,
  fullWidth,
  onChange,
  classNames,
  variant = "text",
  searchable,
  placeholder,
  getValueLabel,
  disabled,
  error,
}: ISelectorProps<T, Meta>) => {
  const [inputValue, setInputValue] = useState("")
  const [open, setOpen] = useState(false)
  const [filteredOptions, setFilteredOptions] = useState<IOption<T, Meta>[]>(options)
  const { refs, floatingStyles, placement, context } = useFloating({
    placement: "bottom-start",
    open: open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
            maxWidth: `${Math.min(Math.max(rects.reference.width, 300), 500)}px`,
          })
        },
        padding: 10,
      }),
    ],
  })
  const click = useClick(context, { event: "mousedown", enabled: !disabled })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, click])
  const selectValue = useMemo(() => {
    if (typeof value === "object") return options?.find((option) => option.value === value.value)?.label ?? ""
    return options?.find((option) => option.value === value)?.label ?? ""
  }, [options, value])

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  useEffect(() => {
    if (searchable && variant !== "text") {
      if (typeof value === "object") setInputValue(value?.label)
      else setInputValue(value ?? "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, searchable, variant])

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={cn(
          `selector-custom selector-custom__${variant}`,
          classNames?.control,
          fullWidth && "selector-custom__full-width",
          disabled && "selector-custom__disabled",
          error && "selector-custom__error",
        )}
      >
        <div>
          {typeof leftSection === "function" ? leftSection(selectValue) : leftSection}
          {searchable && variant !== "text" ? (
            <input
              className="flex-1"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={placeholder}
              onInput={(e) => {
                if (!open) setOpen(true)
                return setFilteredOptions(options.filter((opt) => opt.label.toLowerCase().includes(e.currentTarget.value.toLowerCase())))
              }}
            />
          ) : getValueLabel != null ? (
            getValueLabel(value)
          ) : (
            <span>{selectValue ?? placeholder}</span>
          )}
        </div>
        {rightSection}
        <div className="dropdown-icon">
          <DropdownIcon up={open} />
        </div>
      </div>
      {open && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className={clsx(
            `select-custom__list ${classNames?.list ?? ""} h-fit overflow-hidden flex flex-col`,
            placement.startsWith("top") && "flex-col-reverse",
          )}
        >
          {filteredOptions.length <= 0 && <div className="p-2 text-sm text-gray-500">No options</div>}

          {searchable && variant === "text" && (
            <div>
              <input
                className="text-sm flex-1 text-black w-full px-[20px] py-2.5 border-b"
                placeholder="Search..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onInput={(e) => {
                  if (!open) setOpen(true)
                  return setFilteredOptions(options.filter((opt) => opt.label.toLowerCase().includes(e.currentTarget.value.toLowerCase())))
                }}
              />
            </div>
          )}
          <div className="select-custom__list-wrapper overflow-y-auto">
            {filteredOptions.map((option, index) => {
              const props = {
                className: cn(
                  "select-custom__list-item",
                  variant === "text" ? "min-w-[200px]" : "min-w-[120px]",
                  option.label === selectValue && "selector-custom__selected",
                ),
                key: index,
                onClick: () => {
                  onChange?.(option)
                  setOpen(false)
                },
              }
              return renderOptions != null ? renderOptions(option, props) : <div {...props}>{option.label}</div>
            })}
          </div>
        </div>
      )}
    </>
  )
}

interface IFormSelectProps<T extends FieldValues> extends ISelectorProps {
  control: Control<T>
  required?: boolean
  name: Path<T>
  className?: string
  label?: ReactNode
  onlyValue?: boolean
}

export const FormSelect = <T extends FieldValues>({ required, control, name, className, label, onlyValue, ...props }: IFormSelectProps<T>) => {
  const { $t: t } = useIntl()
  return (
    <Controller
      {...{ control, name, rules: { required: { value: required || false, message: t({ id: "required" }) } } }}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        return (
          <div className={className}>
            {label != null && <label className="mb-1">{label}</label>}
            <Selector
              {...props}
              value={value}
              onChange={(val) => {
                if (onlyValue) onChange(val.value)
                else onChange(val)
                props.onChange?.(val)
              }}
              error={error != null}
            />
            {error != null && <span className="text-red-500 text-xs">{error.message}</span>}
          </div>
        )
      }}
    />
  )
}
